import React from 'react';
import { ShieldCheck, Globe } from 'lucide-react';
import { AppView, RegionInfo, ToolItem } from '../../types';
import { YamooPayLogo } from '../common/YamooPayLogo';

interface GlobalFooterProps {
  onNavigate: (view: AppView) => void;
  onSelectTool: (toolId: string) => void;
  selectedRegion: RegionInfo;
  onSelectRegion: (reg: RegionInfo) => void;
  allRegions: RegionInfo[];
  tools: ToolItem[];
}

export const GlobalFooter: React.FC<GlobalFooterProps> = ({
  onNavigate,
  onSelectTool,
  selectedRegion,
  onSelectRegion,
  allRegions,
}) => {
  return (
    <footer className="bg-white border-t border-[#E5E7EB] pt-14 pb-28 md:pb-16 text-xs text-[#6B7280]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {/* 5-Column Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-10">
          {/* Brand Col */}
          <div className="col-span-2 md:col-span-1 space-y-3">
            <button
              onClick={() => onNavigate('homepage')}
              className="flex items-center text-left focus:outline-hidden cursor-pointer"
            >
              <YamooPayLogo size="md" />
            </button>
            <p className="text-xs leading-relaxed text-[#6B7280]">
              Precision tools for everyday financial decisions. Built on verified data and local jurisdiction rules.
            </p>
            <div className="pt-1 flex items-center gap-2 text-[11px] font-medium text-[#4B5563]">
              <span className="text-base">{selectedRegion.flag}</span>
              <span>{selectedRegion.name}</span>
              <span className="text-[#9CA3AF]">({selectedRegion.currency})</span>
            </div>
          </div>

          {/* Column 1: Tools */}
          <div className="space-y-3">
            <div className="font-bold text-[#1A1A1A] uppercase tracking-wider text-[11px]">
              Tools
            </div>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => onSelectTool('currency-converter')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Currency Converter
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('salary-engine')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Salary & Tax
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('loan-engine')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Loans & Mortgages
                </button>
              </li>
              <li>
                <button
                  onClick={() => onSelectTool('compound-interest-calculator')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Savings & Wealth
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('everyday-command-center')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Everyday Money
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('money-transfers')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Money Transfers
                </button>
              </li>
            </ul>
          </div>

          {/* Column 2: Resources */}
          <div className="space-y-3">
            <div className="font-bold text-[#1A1A1A] uppercase tracking-wider text-[11px]">
              Resources
            </div>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => onNavigate('financial-guides')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Financial Guides
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('help-center')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Help Center
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('methodology')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Methodology & Math
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('glossary')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Financial Glossary
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('currency-intelligence')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Live Exchange Rates
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: Developers */}
          <div className="space-y-3">
            <div className="font-bold text-[#1A1A1A] uppercase tracking-wider text-[11px]">
              Developers
            </div>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => onNavigate('developer-portal')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Developer Platform
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('developer-portal')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  API Documentation
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('developer-portal')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Integration SDKs
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('developer-portal')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Platform Status
                </button>
              </li>
            </ul>
          </div>

          {/* Column 4: Company & Legal */}
          <div className="space-y-3">
            <div className="font-bold text-[#1A1A1A] uppercase tracking-wider text-[11px]">
              Company & Legal
            </div>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => onNavigate('discovery')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  About YamooPay
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('legal-portal')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('legal-portal')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Terms of Service
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('legal-portal')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Cookie Preferences
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('legal-portal')}
                  className="hover:text-[#1A1A1A] text-left transition-colors cursor-pointer"
                >
                  Accessibility (WCAG 2.2)
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Currency Rates Snapshot Strip */}
        <div className="pt-6 border-t border-[#E5E7EB] flex items-center justify-between flex-wrap gap-4 text-[11px]">
          <div className="flex items-center gap-2">
            <Globe className="w-3.5 h-3.5 text-[#1A1A1A]" />
            <span className="font-bold text-[#1A1A1A]">Verified FX Benchmarks:</span>
          </div>
          <div className="flex items-center gap-4 flex-wrap font-mono text-[#4B5563]">
            <span>EUR/USD 1.0815</span>
            <span>GBP/USD 1.2640</span>
            <span>USD/CAD 1.3580</span>
            <span>USD/JPY 154.20</span>
            <span>USD/INR 83.45</span>
            <span>EUR/XAF 655.957</span>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="p-4 bg-[#FBFBFC] rounded-2xl border border-[#E5E7EB] space-y-1.5 text-[11px] leading-relaxed text-[#6B7280]">
          <div className="font-bold text-[#1A1A1A] flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
            <span>Financial & Jurisdictional Notice</span>
          </div>
          <p>
            YamooPay provides financial simulation, currency conversion, and tax modeling tools for informational and educational purposes. Calculation models reflect official statutory rules (such as IRS, FTB, HMRC, CRA, and European tax authorities). Results do not constitute certified legal, tax, or investment advice.
          </p>
        </div>

        {/* Bottom Copyright & Legal Links */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-[#9CA3AF]">
          <div>
            © 2026 YamooPay. All rights reserved.
          </div>
          <div className="flex items-center gap-5">
            <button onClick={() => onNavigate('legal-portal')} className="hover:text-[#1A1A1A] transition-colors cursor-pointer">Privacy</button>
            <button onClick={() => onNavigate('legal-portal')} className="hover:text-[#1A1A1A] transition-colors cursor-pointer">Terms</button>
            <button onClick={() => onNavigate('legal-portal')} className="hover:text-[#1A1A1A] transition-colors cursor-pointer">Cookies</button>
            <button onClick={() => onNavigate('legal-portal')} className="hover:text-[#1A1A1A] transition-colors cursor-pointer">Accessibility</button>
          </div>
        </div>
      </div>
    </footer>
  );
};
